// @flow

// Vendors
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

// Components
import { SmartBidBidsFilters, SmartBidBidsTable } from '.';
import { Loading, LoadMore, ItemsEmpty } from '../../components';

// Requests
import { fetchSmartBidBids } from '../../../requests';

// Hooks
import { useTranslate } from '../../hooks';

const SmartBidBids = () => {
  const initialFilters = {
    keywords: '',
    location: ''
  };
  const itemsPerPage = 12;
  const [pageNumber, setPageNumber] = useState(1);
  const [bids, setBids] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [locations, setLocations] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const content = useTranslate({
    smartBidBidsTitle: 'Search Bids',
    emptyItemsTitle: 'No Matching Bids.',
    emptyItemsMessage: `Your search didn't return any results. Please adjust the filters to see some bids.`,
    emptyItemsActionLabel: 'Clear Filters',
    tableRowLabelProjectName: 'Project Name',
    tableRowLabelLocation: 'Location',
    tableRowLabelDueDate: 'Due Date',
    loadMoreLabel: 'Show More Bids',
    filtersLabelSearch: 'Search Keywords',
    filtersLabelLocation: 'Select a Location'
  });

  const filteredBids = bids.filter(({ title, city, state }, index) => {
    const filterByKeywords = filters.keywords
      ? title.toLowerCase().indexOf(filters.keywords.toLowerCase()) > -1
      : true;

    const filterByLocation = filters.location
      ? `${state.trim()}` === filters.location
      : true;

    return filterByKeywords && filterByLocation;
  });

  const pagedBids = filteredBids.filter(
    (bid, index) => index < itemsPerPage * pageNumber
  );
  const hasLoadMore = pagedBids < filteredBids;
  const showEmpty = !isLoading && pagedBids.length < 1;

  const handleFilterChange = ({ target }) => {
    setFilter(target.name, target.value);
  };

  const resetFilters = () => {
    setFilters({
      ...initialFilters,
      keywords: '',
      location: ''
    });
    setPageNumber(1);
  };

  const setFilter = (name, value = '') => {
    setFilters({ ...filters, [name]: value });
    setPageNumber(1);
  };

  const resetFilter = name => {
    setFilter(name);
  };

  const loadMoreBids = () => setPageNumber(pageNumber + 1);

  const getLocations = bids =>
    bids
      .filter(
        (value, index, self) =>
          self.findIndex(v => v.state.trim() === value.state.trim()) === index
      )
      .sort((a, b) => (a.state > b.state ? 1 : -1))
      .map(({ city, state }) => ({
        value: `${state.trim()}`,
        label: `${state.trim()}`
      }));

  useEffect(() => {
    setLoading(true);
    fetchSmartBidBids()
      .then(({ data }) => {
        setLoading(false);
        setBids(data);
        setLocations(getLocations(data));
      })
      .catch(response => {
        setLoading(false);
        console.error(response);
      });
  }, []);

  return (
    <>
      <h2 className="u--margin-top-0">{content.smartBidBidsTitle}</h2>
      <SmartBidBidsFilters
        filters={filters}
        locations={locations}
        handleFilterChange={handleFilterChange}
        resetFilter={resetFilter}
        labelSearch={content.filtersLabelSearch}
      />

      {pagedBids.length > 0 && (
        <div className="smartbids-results">
          <div className="smartbids-results__count">
            <strong>
              Showing 1 &ndash; {pagedBids.length} of {filteredBids.length}{' '}
              results
            </strong>
          </div>

          <SmartBidBidsTable
            bids={pagedBids}
            rowLabels={{
              projectName: content.tableRowLabelProjectName,
              location: content.tableRowLabelLocation,
              dueDate: content.tableRowLabelDueDate
            }}
          />
        </div>
      )}

      {showEmpty && (
        <ItemsEmpty
          title={content.emptyItemsTitle}
          message={content.emptyItemsMessage}
          actionLabel={content.emptyItemsActionLabel}
          actionOnClick={resetFilters}
          className="u--padding-bottom-0"
        />
      )}

      {hasLoadMore && (
        <LoadMore label={content.loadMoreLabel} onClick={loadMoreBids} />
      )}

      {isLoading && <Loading />}
    </>
  );
};

export default SmartBidBids;
